import React, { useState } from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import {
  CampaignIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
} from "../components/icons";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import { Tag } from "@chakra-ui/react";
import {
  Button,
  ButtonGroup,
  Container,
  EmptyState,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
} from "../components/wombat-ui";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import CampaignCreationModal from "../components/campaigns/CampaignCreationModal";
import { useToggle } from "react-use";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { CampaignListingFragment, useGetCampaigns } from '../graphql/generated/definitions';

type CampaignItemProps = {
  campaign: CampaignListingFragment;
};

const CampaignItem: React.FC<CampaignItemProps> = React.memo(({ campaign }) => {
  const t = useTranslations();

  return (
    <li className="py-4" id={`campaignItem.${campaign.id}`}>
      <div className="flex items-center space-x-4">
        <div className="flex-1 min-w-0">
          <Link
            to={`/campaigns/${campaign.slug}`}
            id={`${campaign.id}-link`}
            className="text-sm font-medium"
          >
            {campaign.name}
          </Link>
          <div>
            <span className="text-xs font-normal text-light-900 max-w-paragraph">
              {campaign.description || t("campaign.noDescription")}
            </span>
          </div>
        </div>
        <Tag>
          {t(`campaign.reviewsCount`, { reviewsCount: campaign.reviewsCount })}
        </Tag>
      </div>
    </li>
  );
});

const CampaignListScreen: React.FC = () => {
  const t = useTranslations();
  const { isCapableOfInsertingCampaigns, isCapableOfReadingCampaigns } =
    useViewer();

  useBreadcrumbs([
    {
      to: "/campaigns",
      text: t('campaign.campaigns') as string,
    },
  ]);

  const [{ page, q, sortBy, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    q: StringParam,
  });

  const {
    data: campaignsData,
    loading,
    refetch,
  } = useGetCampaigns({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      sortBy: sortBy || "name",
      direction: direction as "ASC" | "DESC",
    },
    fetchPolicy: "cache-and-network",
  });

  const campaigns = campaignsData?.campaigns?.collection || [];
  const itemsCount = campaignsData?.campaigns?.metadata?.totalCount;

  const [search, setSearch] = useState(q);

  const [campaignCreationModalOpen, toggleCampaignCreationModalOpen] =
    useToggle(false);

  return (
    <ScreenContainer
      error={
        isCapableOfReadingCampaigns ? undefined : (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        )
      }
    >
      <ScreenHeader
        title={t("campaign.campaigns") as string}
        description={t("campaign.campaignsDescription") as string}
        icon={CampaignIcon}
        actions={[
          isCapableOfInsertingCampaigns && (
            <Button
              onClick={toggleCampaignCreationModalOpen}
              variant="primary"
              key="insertCampaignButton"
              data-test-id="insertCampaignButton"
            >
              {t("campaign.createCampaign")}
            </Button>
          ),
        ]}
      />
      <Container className="bg-white">
        <List
          isLoading={loading}
          emptyMessage={t("campaign.noMatchingCampaigns") as string}
          data={campaigns}
          renderAsList={(data) => (
            <CampaignItem campaign={data} key={data.id} />
          )}
          listOnly
          id="campaigns-list"
        >
          <SearchInput
            onSubmit={(q) => setQuery({ page: 1, q })}
            label={t("list.search") as string}
            placeholder={t("campaign.searchPlaceholder") as string}
            value={search}
            onChange={setSearch}
          />
          <div className="flex space-x-3">
            <ButtonGroup scale="sm">
              <Button
                icon={SortAscendingDirectionIcon}
                onClick={() => setQuery({ direction: "ASC" })}
                variant={
                  direction === "ASC" || !direction ? "primary" : undefined
                }
              />
              <Button
                icon={SortDescendingDirectionIcon}
                onClick={() => setQuery({ direction: "DESC" })}
                variant={direction === "DESC" ? "primary" : undefined}
              />
            </ButtonGroup>
          </div>
        </List>
        {!loading && (
          <Pagination
            page={page}
            onChange={(page) => setQuery({ page })}
            itemsCount={itemsCount}
            itemsPerPage={20}
          />
        )}
      </Container>
      <CampaignCreationModal
        isOpen={campaignCreationModalOpen}
        onClose={toggleCampaignCreationModalOpen}
        onSubmit={refetch}
      />
    </ScreenContainer>
  );
};

export default CampaignListScreen;
